// ============================================================
// DEPLOYMENT - where combatants appear, and why there.
//
// Before this, a new combatant was dropped on a ring around the arena
// centre with the angle taken from their roster index, and nothing was
// checked: two arrivals could share a spot and either could land inside
// a wall. The GM had no say in it at all.
//
// Now there is a MARKER, planted deliberately, and everyone who arrives
// rings around wherever it stands at that moment. That is the whole
// workflow: plant it, import the players, move it, import the
// opposition. Moving the marker never moves anybody already placed -
// deployment happens on arrival, not continuously.
//
// This module is the geometry and nothing else. It does not know about
// Firestore, React, or the roster; it takes tokens and terrain and
// returns where they should stand.
//
// Pure. Zero React dependency.
// ============================================================
import { boundaryContains, clampToBoundary } from './boundary';
import { surfaceHeightAt } from './reconcile';
import { overlapAt } from './overlap';
import { terrainAt } from './movement';
import type { Boundary } from './boundary';
import type { Solid, Vec3 } from './types';
import { outranking, type Token } from './tokens';

/** Who takes the first slot. */
type RingOrder = 'roster' | 'size';

export interface RingConfig {
  /** Distance from the marker, in metres. */
  radius: number;
  /**
   * Metres between neighbours, or 'auto' to take it from each token's
   * own footprint - so a Dreadnought is not shoulder-to-shoulder with a
   * Guardsman.
   */
  spacing: number | 'auto';
  /** Degrees; 0 is due east, matching `Math.atan2(z, x)`. */
  startAngle: number;
  /** Degrees of arc. 360 is a ring; 90 is a wedge coming through a gate. */
  sweep: number;
  order: RingOrder;
}

export interface DeploymentPoint {
  /**
   * Present from the first version even though only one marker exists.
   * A second marker, or the deployment ZONES wanted later, then becomes
   * an addition rather than a migration.
   */
  id: string;
  x: number;
  z: number;
  /**
   * Which storey it was planted on. Absent means the ground, which is
   * what every marker placed before v7.94.0 means.
   */
  y?: number;
  /**
   * How high ABOVE that storey the banner flies, in metres.
   *
   * Deployment has always settled people onto whatever is underfoot,
   * which is right for troops walking on and wrong for anything that
   * arrives in the air - a drop, a flyer, a squad on a gantry that is
   * not modelled. Raised, the ring puts them AT this height and does not
   * hunt for a surface: being off the ground is the intent, not a fault
   * to correct (v8.31.0).
   *
   * Absent or 0 is the ground, which is what every marker before this
   * means and what the ⤓ button returns to.
   */
  air?: number;
  ring: RingConfig;
  /**
   * The team everyone who arrives under this marker is stamped with
   * (v8.315.0). Absent means arrivals keep whatever their sheet says. The
   * workflow is "plant it, import the players, move it, import the
   * opposition" - so a team per pass is one field on the flag, set before
   * the pass and cleared or changed for the next.
   */
  team?: {
    tag: string;
    label: string;
    livery?: 'steel' | 'cobalt' | 'verdigris' | 'magenta' | 'violet' | `#${string}`;
  };
}

/**
 * A team's own arrival point, by team tag (plan scene-editor-library, B; ruling 2026-10-20): newcomers of that team
 * ring THERE, anyone else at the main marker. Empty is today's single marker. Kept beside the main point - in a
 * scene's record and file, and at the table in `shared/arrivals` - never inside `shared/deployment`, so a client that
 * knows nothing of it reads the main marker exactly as before.
 */
export type TeamArrivals = Record<string, DeploymentPoint>;

/** Where someone of this team arrives: their team's point when there is one, else the main marker (or none). */
export function arrivalFor(
  teamTag: string | null | undefined,
  marker: DeploymentPoint | null,
  arrivals: TeamArrivals | null | undefined,
): DeploymentPoint | null {
  return (teamTag && arrivals?.[teamTag]) || marker;
}

const isPoint = (v: unknown): v is DeploymentPoint =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as DeploymentPoint).x === 'number' &&
  typeof (v as DeploymentPoint).z === 'number';

/** Stored team points, read tolerantly: each placed point kept under its (non-blank) tag, anything else dropped. */
export function readTeamArrivals(raw: unknown): TeamArrivals {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw).filter(([tag, point]) => tag.trim() && isPoint(point)));
}

/** Each team's point onto a new map, as `preserveMarker` keeps the main one; the SAME object when none moved. */
export function preserveTeamArrivals(
  arrivals: TeamArrivals,
  incoming: { boundary?: Boundary } | null | undefined,
): TeamArrivals {
  let moved = false;
  const next = Object.fromEntries(
    Object.entries(arrivals).map(([tag, point]) => {
      const kept = preserveMarker(point, incoming) ?? point;
      moved ||= kept !== point;
      return [tag, kept];
    }),
  );
  return moved ? next : arrivals;
}

export interface AvoidOptions {
  /** Never place anyone inside a solid volume. */
  avoidStructures?: boolean;
  /** Never place anyone on ground movement calls impassable. */
  avoidImpassable?: boolean;
  /** Never place anyone within another combatant's footprint. */
  avoidCombatants?: boolean;
  /** Hidden terrain counts as terrain for the GM. */
  includeHidden?: boolean;
}

export interface Deployment {
  /** Token id → the token, positioned, with its rev bumped. */
  placed: Record<string, Token>;
  /** Ids that had to be pulled back inside the arena. */
  clamped: string[];
  /** Ids placed somewhere avoidance would have rejected. */
  unavoided: string[];
}

/** A sensible ring: a full circle, close in, everyone looking out. */
export function defaultRing(): RingConfig {
  return { radius: 3, spacing: 'auto', startAngle: 0, sweep: 360, order: 'roster' };
}

/**
 * A marker at the arena's centre: where a scene's own cast stand when nothing says otherwise - a cut's unmarked cast
 * and the Scene Editor's unplaced cast alike (2026-10-28), so the editor shows where the cut puts them.
 */
export function centreMarker(boundary: Boundary): DeploymentPoint {
  return { id: 'stage', x: boundary.centre.x, z: boundary.centre.z, ring: defaultRing() };
}

const DEG = Math.PI / 180;

/**
 * How far up a deployment may step, when the token does not say.
 *
 * One stride for everybody, the same simplification `reconcileTokens`
 * makes: deployment runs before anyone's traits are to hand, and the
 * answer only decides whether a doorstep counts as standable.
 */
const DEFAULT_DEPLOY_STEP = 0.6;

/** How much room this token wants, centre to centre. */
function spacingFor(config: RingConfig, token?: Token): number {
  if (config.spacing !== 'auto') return Math.max(0.1, config.spacing);
  // Two footprints plus a little air. A footprint is a radius, so a pair
  // of average combatants ends up a bit over a metre apart.
  return Math.max(0.6, (token?.footprint ?? 0.5) * 2 + 0.2);
}

/**
 * How many fit on a ring of this radius at this spacing.
 *
 * At least one, or a tight ring would hold nobody and the spiral below
 * would never advance.
 */
function capacity(radius: number, spacing: number, sweep: number): number {
  const arc = sweep * DEG * radius;
  return Math.max(1, Math.floor(arc / spacing) + (sweep >= 360 ? 0 : 1));
}

/**
 * Where `count` combatants stand around the marker.
 *
 * Concentric rings, stepping outward by the spacing once one is full -
 * eleven combatants become eight and three rather than eleven on top of
 * each other. A full circle deliberately does NOT place anyone at both
 * 0° and 360°, which would be the same spot twice.
 */
export function ringSlots(marker: DeploymentPoint, count: number, config: RingConfig): Vec3[] {
  if (count <= 0) return [];
  const out: Vec3[] = [];
  const spacing = spacingFor(config);
  let radius = Math.max(0.5, config.radius);
  let remaining = count;

  while (remaining > 0) {
    const room = Math.min(remaining, capacity(radius, spacing, config.sweep));
    out.push(...ringPoints(marker, radius, room, config));
    remaining -= room;
    radius += spacing;
  }

  return out;
}

/**
 * `room` points spread along one ring's arc.
 *
 * A ring of one sits at the start angle rather than in the middle of
 * the arc: with a single combatant the arc's midpoint is arbitrary.
 */
function ringPoints(marker: DeploymentPoint, radius: number, room: number, config: RingConfig): Vec3[] {
  const steps = config.sweep >= 360 ? room : Math.max(1, room - 1);
  return Array.from({ length: room }, (_, i) => {
    const t = room === 1 ? 0 : i / steps;
    const angle = (config.startAngle + t * config.sweep) * DEG;
    return { x: marker.x + Math.cos(angle) * radius, y: 0, z: marker.z + Math.sin(angle) * radius };
  });
}

/**
 * Is this spot acceptable under the switches that are on?
 *
 * `at.y` MUST BE THE STANDING ELEVATION, not the ground plane. A floor is
 * something you stand ON: probing at y=0 puts the probe inside it, every
 * candidate reads as blocked, and the spiral runs outward until it leaves
 * the floor - which is the whole map. That shipped, and looked like
 * "Re-deploy everyone puts them all at the very edge" (v7.61.0).
 */
function acceptable(
  at: Vec3,
  solids: Solid[],
  options: AvoidOptions,
  taken: { x: number; z: number; footprint: number }[],
  footprint: number,
): boolean {
  if (options.avoidStructures && overlapAt(at, solids, options.includeHidden ?? true).blocking.length > 0) return false;
  if (options.avoidImpassable && terrainAt(at.x, at.z, solids) === 'impassable') return false;
  return !(options.avoidCombatants && crowded(at, taken, footprint));
}

/** Would a body this size here overlap anyone already placed? */
function crowded(at: Vec3, taken: { x: number; z: number; footprint: number }[], footprint: number): boolean {
  return taken.some((t) => Math.hypot(t.x - at.x, t.z - at.z) < t.footprint + footprint);
}

/** Ordering for the slots - roster order, or the big models outward. */
function ordered(tokens: Token[], order: RingOrder): Token[] {
  if (order !== 'size') return tokens;
  // Ascending, so the LAST slots - the outer ring - go to the largest.
  return [...tokens].sort((a, b) => (a.footprint ?? 0) - (b.footprint ?? 0));
}

/**
 * Put these combatants on the map around the marker.
 *
 * Returns new tokens; it mutates nothing. Anyone the arena cannot hold
 * is pulled to its edge, and anyone avoidance cannot satisfy is placed
 * anyway and named in `unavoided` - refusing to place a combatant is
 * worse than placing them somewhere awkward, which a GM can see and drag
 * out of.
 *
 * `ignoresTerrain` skips avoidance entirely: a flyer is allowed to be
 * over a wall, the same exemption `reconcileTokens` already makes.
 */
export function deploy(
  tokens: Token[],
  marker: DeploymentPoint,
  boundary: Boundary,
  solids: Solid[],
  options: AvoidOptions = {},
  groundAt: (x: number, z: number) => number = () => 0,
  /**
   * Where they should stand, if not a ring.
   *
   * A FORMATION DRAG IS A DEPLOYMENT IN EVERY OTHER RESPECT - clamped to
   * the boundary, settled onto whatever surface is underfoot, stepped
   * aside from anyone already standing there. Only the shape differs, so
   * only the shape is a parameter, and a line inherits the whole of the
   * settling machinery for free (v8.22.0).
   */
  slotsOverride?: Vec3[],
): Deployment {
  const config = marker.ring ?? defaultRing();
  const queue = ordered(tokens, config.order);
  const slots = slotsOverride ?? ringSlots(marker, queue.length, config);
  const ctx = deployContext({ marker, boundary, solids, options, groundAt, spacing: spacingFor(config) });
  const result: Deployment = { placed: {}, clamped: [], unavoided: [] };
  const taken: Taken[] = [];
  queue.forEach((token, i) => placeOne(ctx, token, slots[i], { taken, result }));
  return result;
}

/** Somebody already placed, as far as avoidance cares. */
type Taken = { x: number; z: number; footprint: number };

/** Everything one `deploy` call decides once and every arrival reads. */
interface DeployContext {
  marker: DeploymentPoint;
  boundary: Boundary;
  solids: Solid[];
  options: AvoidOptions;
  groundAt: (x: number, z: number) => number;
  spacing: number;
  /** The level the marker itself stands at. */
  markerFloor: number;
  /** How far above that floor the banner flies; 0 is the ground. */
  air: number;
}

/**
 * The level the marker itself stands at: a candidate is judged as a
 * step FROM there, so a floor is reachable and a wall is not.
 *
 * A MARKER PLANTED ON A GANTRY BRINGS ITS LEVEL WITH IT. Without that,
 * "a roof is not a deployment spot" - correct for keeping people off
 * the top of a wall - also made it impossible to deploy onto a
 * platform on purpose. Saying where the marker stands is how a GM says
 * which storey they mean, and the rule below then works unchanged: it
 * fills that level and still refuses the one above it (v7.94.0).
 *
 * A RAISED BANNER DEPLOYS INTO THE AIR. Nothing underfoot is consulted
 * and nothing is stepped around: what is below a flyer is not in its
 * way. The ring's shape, order and boundary all still apply.
 */
function deployContext(ctx: Omit<DeployContext, 'markerFloor' | 'air'>): DeployContext {
  const { marker, solids, groundAt } = ctx;
  const markerFloor =
    marker.y !== undefined ? marker.y : surfaceHeightAt(marker.x, marker.z, solids, groundAt(marker.x, marker.z));
  return { ...ctx, markerFloor, air: Math.max(0, marker.air ?? 0) };
}

/** Place one arrival, recording what happened to them. */
function placeOne(
  ctx: DeployContext,
  token: Token,
  slot: Vec3,
  { taken, result }: { taken: Taken[]; result: Deployment },
): void {
  const footprint = token.footprint ?? 0.5;
  let spot = slot;
  if (avoids(ctx, token)) {
    const found = searchOutward(ctx, slot, taken, footprint);
    if (found) spot = found;
    else result.unavoided.push(token.id);
  }
  if (!boundaryContains(ctx.boundary, spot.x, spot.z)) {
    const p = clampToBoundary(ctx.boundary, spot.x, spot.z);
    spot = { x: p.x, y: 0, z: p.z };
    result.clamped.push(token.id);
  }
  result.placed[token.id] = {
    ...token,
    x: spot.x,
    z: spot.z,
    elevation: elevationFor(ctx, token, spot),
    // A placement for the whole table outranks any drag in flight on the token (tokens `outranking`).
    rev: outranking(token.rev),
  };
  taken.push({ x: spot.x, z: spot.z, footprint });
}

/** Does this arrival go looking for a better spot? Not in the air, not a flyer, not with every switch off. */
function avoids(ctx: DeployContext, token: Token): boolean {
  if (ctx.air > 0 || token.ignoresTerrain) return false;
  const { avoidStructures, avoidImpassable, avoidCombatants } = ctx.options;
  return !!(avoidStructures || avoidImpassable || avoidCombatants);
}

/**
 * Step outward along the slot's bearing until something passes, or null.
 * The search is bounded by the arena, so a marker in a dense corner
 * cannot spin for ever.
 */
function searchOutward(ctx: DeployContext, slot: Vec3, taken: Taken[], footprint: number): Vec3 | null {
  const { marker, boundary } = ctx;
  const bearing = Math.atan2(slot.z - marker.z, slot.x - marker.x);
  const start = Math.hypot(slot.x - marker.x, slot.z - marker.z);
  const limit = boundary.radius * Math.max(boundary.scaleX, boundary.scaleZ) * 2;
  for (let r = start; r <= limit; r += ctx.spacing) {
    const candidate = standingSpot(ctx, bearing, r);
    if (candidate && acceptable(candidate, ctx.solids, ctx.options, taken, footprint)) return candidate;
  }
  return null;
}

/**
 * Where their FEET would be at `r` metres along `bearing`, or null when
 * that is outside the arena or up on a roof.
 *
 * Probing at y=0 instead treats the FLOOR as something you are inside:
 * every candidate reads blocked and the spiral runs outward until it
 * leaves the floor, which is the whole map. That shipped, and looked
 * like "Re-deploy puts everyone at the very edge" (v7.61.0).
 *
 * A ROOF IS NOT A DEPLOYMENT SPOT. `reachableSurface`'s footing is no
 * use here: it carries you up onto anything whose base is at or below
 * your feet, which is right for terrain RISING under somebody and wrong
 * for choosing where to put them - it would deploy people onto the top
 * of a three-metre wall. The rule this wants is plainer, so it is
 * written plainly. A Token carries no stride of its own, so the step is
 * `DEFAULT_DEPLOY_STEP`, as in `reconcileTokens`.
 */
function standingSpot(ctx: DeployContext, bearing: number, r: number): Vec3 | null {
  const x = ctx.marker.x + Math.cos(bearing) * r;
  const z = ctx.marker.z + Math.sin(bearing) * r;
  if (!boundaryContains(ctx.boundary, x, z)) return null;
  const standing = surfaceHeightAt(x, z, ctx.solids, ctx.groundAt(x, z));
  if (standing > ctx.markerFloor + DEFAULT_DEPLOY_STEP + 1e-9) return null;
  return { x, y: standing, z };
}

/**
 * The height an arrival stands at.
 *
 * FROM THE MARKER'S OWN STOREY. Without a footing `surfaceHeightAt`
 * takes the HIGHEST surface over the spot, so anyone deployed beneath a
 * gantry was silently stood on top of it - which is most of what
 * "sometimes they are under the platform and sometimes on it" was.
 * Judged as a step from where the marker stands, a floor is reachable
 * and a roof overhead is not (v7.94.0).
 *
 * IN THE AIR, THE HEIGHT IS THE POINT. Settling onto a surface is what
 * deployment does for troops walking on; for anyone arriving above the
 * plane it would undo the whole gesture (v8.31.0).
 */
function elevationFor(ctx: DeployContext, token: Token, spot: Vec3): number {
  if (ctx.air > 0) return ctx.markerFloor + ctx.air;
  if (token.ignoresTerrain) return token.elevation;
  return surfaceHeightAt(spot.x, spot.z, ctx.solids, ctx.groundAt(spot.x, spot.z), {
    from: ctx.markerFloor,
    maxStep: DEFAULT_DEPLOY_STEP,
  });
}

/**
 * What the marker should be after a map is loaded.
 *
 * THE MARKER SURVIVES THE SWAP - it is session state, not part of the
 * terrain document, so loading a map cannot overwrite it. Two things
 * still have to be decided, and they are decided here rather than inside
 * a callback so they can be tested:
 *
 * - a map that carries its own suggestion is honoured ONLY when the
 *   session has no marker, so an authored map can propose where a fight
 *   begins without overriding one somebody planted;
 * - a marker the new arena does not contain is clamped in, because a
 *   marker outside the map is a marker nobody can use.
 *
 * Returns the SAME OBJECT when nothing needs to change, so the caller can
 * use identity to decide whether to write.
 */
export function preserveMarker(
  current: DeploymentPoint | null | undefined,
  incoming: { boundary?: Boundary; deployment?: unknown } | null | undefined,
): DeploymentPoint | null {
  if (!current) return (incoming?.deployment as DeploymentPoint) ?? null;
  const arena = incoming?.boundary;
  if (!arena || boundaryContains(arena, current.x, current.z)) return current;
  const p = clampToBoundary(arena, current.x, current.z);
  return { ...current, x: p.x, z: p.z };
}

/**
 * Positions along a line, one per step, for a formation drag.
 *
 * A FIRING LINE ALONG A WALL is the thing a ring cannot do, and the
 * reason this exists beside `ringSlots` rather than as an option on it:
 * a ring is defined by its centre and a line by its ends, and folding
 * them together would give a config where half the fields are ignored
 * (v8.22.0).
 *
 * EVENLY SPACED BETWEEN THE ENDS, not stamped every `step` metres from
 * the start. Whoever drew the line chose where it finishes, and a
 * formation that stops short of the pointer because the arithmetic ran
 * out reads as a bug. `step` only decides HOW MANY fit.
 *
 * One body is placed at the start: with nowhere to spread to, the line
 * is a point, and the point the drag began at is the one that was
 * chosen deliberately.
 */
export function lineSlots(from: Vec3, to: Vec3, count: number, step = 1): Vec3[] {
  if (count <= 0) return [];
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const length = Math.hypot(dx, dz);
  if (count === 1 || length < 1e-9) return [{ x: from.x, y: from.y ?? 0, z: from.z }];

  // How many the line has room for, at one per step - the gaps between
  // n bodies are n-1, which is what makes a two-metre line hold three at
  // a one-metre step rather than two.
  const room = Math.floor(length / Math.max(0.01, step)) + 1;
  const placed = Math.min(count, Math.max(2, room));
  const out: Vec3[] = [];
  for (let i = 0; i < placed; i++) {
    const t = i / (placed - 1);
    out.push({ x: from.x + dx * t, y: from.y ?? 0, z: from.z + dz * t });
  }
  return out;
}

export interface ScreenBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Normalise a drag into a box, whichever way it was drawn.
 *
 * A sweep from bottom-right to top-left is the same sweep; making every
 * caller remember that is how one of them forgets.
 */
export function sweepBox(from: { x: number; y: number }, to: { x: number; y: number }): ScreenBox {
  return {
    x1: Math.min(from.x, to.x),
    y1: Math.min(from.y, to.y),
    x2: Math.max(from.x, to.x),
    y2: Math.max(from.y, to.y),
  };
}

/** Did the drag travel far enough to mean a sweep rather than a click? */
export function isSweep(box: ScreenBox, slop = 6): boolean {
  return box.x2 - box.x1 > slop || box.y2 - box.y1 > slop;
}

/**
 * Who is inside the swept box.
 *
 * SCREEN COORDINATES, not world ones: the box is drawn on a projected
 * scene, so a world-space test would take in people the GM can see are
 * outside it - which is the only test that matters for a gesture aimed
 * with the eye (v8.24.0).
 *
 * The FOOT is what counts, because that is where a marker stands; a
 * body leaning into the box is not in it.
 */
export function sweptIds(feet: { id: string; x: number; y: number }[], box: ScreenBox): string[] {
  return feet.filter((f) => f.x >= box.x1 && f.x <= box.x2 && f.y >= box.y1 && f.y <= box.y2).map((f) => f.id);
}

/**
 * Positions filling a rectangle, row by row.
 *
 * The third shape, after the ring and the line: a squad in a block,
 * which is what an area drag means. One body per `step`, rows nearest
 * the drag's start first, so the front rank is where you began (v8.25.0).
 *
 * THE BOX IS INCLUSIVE OF BOTH CORNERS, like the line's ends: you drew
 * to there, so there is inside. A box too small for two bodies still
 * holds one - the corner you started at.
 */
export function areaSlots(from: Vec3, to: Vec3, count: number, step = 1): Vec3[] {
  if (count <= 0) return [];
  const gap = Math.max(0.01, step);
  const xs = axisPositions(from.x, to.x, gap);
  const zs = axisPositions(from.z, to.z, gap);
  const out: Vec3[] = [];
  for (const z of zs) {
    for (const x of xs) {
      if (out.length >= count) return out;
      out.push({ x, y: from.y ?? 0, z });
    }
  }
  return out;
}

/**
 * The positions along one side of the box, one per `gap`.
 *
 * CENTRED IN THE BOX. A grid stamped from one corner leaves a ragged
 * margin on the far side whenever the box is not a whole number of steps
 * wide, and a squad that hugs one edge does not read as filling the area
 * that was drawn.
 *
 * Nearest the start of the drag first: whichever edge you began at is
 * the front rank.
 */
function axisPositions(start: number, end: number, gap: number): number[] {
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  const n = Math.max(1, Math.floor((hi - lo) / gap) + 1);
  const first = lo + (hi - lo - (n - 1) * gap) / 2;
  const near = Math.abs(start - lo) <= Math.abs(start - hi);
  return Array.from({ length: n }, (_, i) => first + (near ? i : n - 1 - i) * gap);
}

/** How many an area has room for, at one per step. */
export function areaCapacity(from: Vec3, to: Vec3, step = 1): number {
  const gap = Math.max(0.01, step);
  const cols = Math.max(1, Math.floor(Math.abs(to.x - from.x) / gap) + 1);
  const rows = Math.max(1, Math.floor(Math.abs(to.z - from.z) / gap) + 1);
  return cols * rows;
}
